import { useFormik } from 'formik'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { createEspecialidade, editEspecialidade } from '../../core/_requests'
import { EspecialidadeView } from '../../core/_models'
import {
  AtendimentoType,
  ButtonType,
  IconPaths,
  IconSize,
  OptionsHelper,
  showAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import {
  Button,
  ButtonIcon,
  FetchState,
  FormInput,
  FormSelect,
  FormToggle,
  IOption,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import { especialidadeSchema } from './validation/especialidadeSchema'
import { useT } from '_metronic/i18n/Metronici18n'
import { ConselhosModal } from 'app/modules/apps/admin/profissionais-saude/profissional-saude/components/modal/ConselhosModal'
import { getConselhos } from 'app/modules/apps/admin/profissionais-saude/profissional-saude/core/_requests'

export const EspecialidadeModal = () => {
  const { closeModal, showModal, fetchStates } = useModal()
  const { refetch } = useQueryResponse()
  const { t } = useT()

  const { data: especialidade } = (fetchStates['especialidade'] ||
    {}) as FetchState<EspecialidadeView>

  const formik = useFormik({
    initialValues: {
      nome: especialidade?.nome || '',
      assinaturaPacienteObrigatoria: especialidade?.assinaturaPacienteObrigatoria || false,
      tiposAtendimento: especialidade?.tiposAtendimentoMap
        ? OptionsHelper.createOptions(especialidade.tiposAtendimentoMap, 'descricao', 'name')
        : [],
      conselho: especialidade?.conselho
        ? {
            label: `${especialidade.conselho.sigla} - ${especialidade.conselho.nome}`,
            value: especialidade.conselho.id,
          }
        : (undefined as IOption<number> | undefined),
    },
    validationSchema: especialidadeSchema,

    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      const data = {
        ...values,
        tiposAtendimento: values.tiposAtendimento.map(
          (option) => option.value
        ) as AtendimentoType[],
        conselho: values.conselho?.value,
      }

      try {
        if (especialidade?.id) {
          await editEspecialidade({ ...data, id: especialidade.id })
        } else {
          await createEspecialidade(data)
        }

        showAlert({
          title: 'Sucesso!',
          text: `Especialidade ${especialidade?.id ? 'editada' : 'cadastrada'} com sucesso!`,
          icon: 'success',
        })
        refetch()
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const openConselhosModal = () => {
    showModal({
      title: 'Gerenciar Conselhos',
      content: <ConselhosModal />,
      fetchers: {
        conselhos: () => getConselhos(),
      },
    })
  }

  return (
    <form noValidate onSubmit={formik.handleSubmit}>
      <FormInput
        formik={formik}
        label='Nome da Especialidade'
        name='nome'
        placeholder='Digite o nome da especialidade'
        className='mb-3'
        isRequired
      />
      <FormSelect
        formik={formik}
        label='Tipo de Atendimento'
        placeholder='Selecione um ou mais tipos de atendimento'
        fetchOptions={OptionsHelper.getOptionsTiposAtendimento}
        name='tiposAtendimento'
        className='mb-3'
        isDisabled={formik.isSubmitting}
        isMulti
        isRequired
      />
      <div className='d-flex align-items-end mb-3'>
        <FormSelect
          formik={formik}
          name='conselho'
          label='Conselho'
          fetchOptions={OptionsHelper.getOptionsConselhos}
          className='flex-grow-1'
          isDisabled={formik.isSubmitting}
          placeholder='Selecione um conselho'
        />
        <ButtonIcon
          icon={IconPaths.Gear}
          iconSize={IconSize.Small}
          onClick={openConselhosModal}
          disabled={formik.isSubmitting}
        />
      </div>
      <div className='d-flex flex-end align-items-center pt-5'>
        <FormToggle
          formik={formik}
          label='Requer Assinatura do Paciente'
          name='assinaturaPacienteObrigatoria'
        />
      </div>
      <div className='d-flex flex-end pt-5'>
        <Button
          className='btn-secondary me-2'
          type={ButtonType.Reset}
          onClick={closeModal}
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        <Button className='btn-primary' type={ButtonType.Submit} disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>
    </form>
  )
}
